import React, { useEffect, useLayoutEffect, useState } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import moment from "moment";
import { useForm } from "react-hook-form";
import { convertTo12HourFormat } from "Utils/utils";
import { apiPostRequest } from "helpers/Requests";
import { URL_SP_CHECK_WORKER_LEAVE_SHIFT_OVERLAP } from "store/services/URL";

const LeaveActionModal = ({
  isShow,
  handleClose,
  leave,
  action,
  handleConfirm,
  isSubmitting = false,
}) => {
  const [rejection_notes, setRejectionNotes] = useState("");
  const [shiftOverlap, setShiftOverlap] = useState(null);
  const [shiftCheckLoading, setShiftCheckLoading] = useState(false);

  const { watch } = useForm();

  useLayoutEffect(() => {
    if (isShow) {
      setRejectionNotes("");
      setShiftOverlap(null);
      if (action === "approved" && leave?.worker_id) {
        setShiftCheckLoading(true);
      } else {
        setShiftCheckLoading(false);
      }
    }
  }, [isShow, leave, action]);

  useEffect(() => {
    const runCheck = async () => {
      if (!isShow || !leave || action !== "approved" || !leave.worker_id) {
        return;
      }
      setShiftCheckLoading(true);
      try {
        const res = await apiPostRequest(
          URL_SP_CHECK_WORKER_LEAVE_SHIFT_OVERLAP,
          {
            worker_id: leave.worker_id,
            start_date: leave.start_date,
            end_date: leave.end_date,
            is_partial_leave: !!leave.is_partial_leave,
            start_time: leave.start_time ?? null,
            end_time: leave.end_time ?? null,
          }
        );
        const data = res?.data?.data ?? res?.data ?? res;
        setShiftOverlap(data);
      } catch {
        setShiftOverlap(null);
      } finally {
        setShiftCheckLoading(false);
      }
    };
    runCheck();
  }, [isShow, leave, action]);

  const footerWaitingOnOverlap =
    action === "approved" && shiftCheckLoading;

  const footerDisabled = footerWaitingOnOverlap || isSubmitting;

  const modalFooter = () => (
    <>
      <CustomButton
        label={`Yes, ${action === "approved" ? "Approve" : "Reject"} Leave`}
        variant={action === "approved" ? "primary" : "danger"}
        isDisabled={footerDisabled}
        showLoading={isSubmitting}
        clickHandler={async () => {
          await handleConfirm(rejection_notes);
        }}
      />
      <CustomButton
        label='No, cancel'
        isDisabled={footerDisabled}
        clickHandler={handleClose}
      />
    </>
  );

  const actionLabels = {
    approved: "Approve",
    rejected: "Reject",
  };

  return (
    <>
      {isShow && (
        <ModalSkeleton
          title={`Confirm ${action === "approved" ? "Approval" : "Rejection"}`}
          cssClass='w-[50%]'
          isOpen={isShow}
          closeModal={handleClose}
          modalFooter={modalFooter()}
        >
          <div>
            <p className='mb-3 max-w-2xl text-sm text-gray-500'>
              Details of Leave Request from{" "}
              <span className='text-gray-900'>
                {leave?.worker?.first_name || ""}{" "}
                {leave?.worker?.last_name || ""}
              </span>
            </p>

            {action === "approved" && shiftCheckLoading && (
              <p className='mb-3 text-sm text-gray-500'>
                Checking scheduled shifts…
              </p>
            )}

            {action === "approved" &&
              shiftOverlap?.has_scheduled_shifts &&
              !shiftCheckLoading && (
                <div
                  className='mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
                  role='alert'
                >
                  <p className='font-medium'>Scheduled shifts overlap leave request</p>
                  {/* <p className='mt-1'>{shiftOverlap.message}</p> */}
                  {shiftOverlap.overlap_count > 0 && (
                    <p className='mt-1 text-xs'>
                      {shiftOverlap.overlap_count} shift{shiftOverlap.overlap_count === 1 ? "" : "s"} on the roster for these dates.
                    </p>
                  )}
                  {Array.isArray(shiftOverlap.overlapping_shift_dates) &&
                    shiftOverlap.overlapping_shift_dates.length > 0 && (
                      <p className='mt-2 text-xs'>
                        Dates:{" "}
                        {shiftOverlap.overlapping_shift_dates
                          .map((d) => moment(d).format("DD/MM/YYYY"))
                          .join(", ")}
                      </p>
                    )}
                </div>
              )}

            <div>
              {/* Worker Name */}
              {/* <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>Name</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  {`${leave?.worker?.first_name || ""} ${
                    leave?.worker?.last_name || ""
                  }`}
                </dd>
              </div> */}
 <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>
                Leave Type
                </dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                 {
                  leave?.is_partial_leave ? "Partial Leave" : "Full Day Leave"
                 }
                </dd>
              </div>
              {/* Leave From */}
              <div className='bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>
                  Leave From
                </dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  {moment(leave?.start_date).format("DD/MM/YYYY")} {" "}
                   {leave.is_partial_leave
                                                   ? `${convertTo12HourFormat(leave?.start_time)}`
                                                   : ""}
                </dd>
              </div>

              {/* Leave To */}
              <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>Leave To</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  {moment(leave?.end_date).format("DD/MM/YYYY")} {" "}
                      {leave.is_partial_leave ? `${convertTo12HourFormat(leave?.end_time)}`
                                                      : ""}
                </dd>
              </div>

              {/* Reason */}
              <div className='bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>Reason</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  {leave?.details || "—"}
                </dd>
              </div>

              {/* Current Status */}
              {/* <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                <dt className='text-sm font-medium text-gray-500'>
                  Current Status
                </dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  {leave?.status?.charAt(0).toUpperCase() +
                    leave?.status?.slice(1)}
                </dd>
              </div> */}
            </div>

            {/* Optional comments only for rejection */}

            <div className='mt-4'>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Comments (optional)
              </label>
              <textarea
                value={rejection_notes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                disabled={footerDisabled}
                className='w-full border rounded p-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70'
                maxLength={500}
                rows={3}
                placeholder={
                  action === "approved"
                    ? "Enter approval notes"
                    : "Enter rejection notes"
                }
              />
            </div>
            <div className='text-right text-xs text-gray-500 mt-1'>
              {500 - (rejection_notes.length || 0)} characters remaining
            </div>

            <p className='text-gray-700'>
              Are you sure you want to{" "}
              <span
                className={
                  action === "approved"
                    ? "text-green-600 font-semibold"
                    : "text-red-600 font-semibold"
                }
              >
                {actionLabels[action]}
              </span>{" "}
              this leave request?
            </p>
          </div>
        </ModalSkeleton>
      )}
    </>
  );
};

export default LeaveActionModal;
